import React, { useState } from 'react';
import { IconClose } from '../../assets/svg/icons';
import {
  StyledCheckoutBody,
  StyledCheckoutSection,
  StyledCheckoutSectionTitle,
  StyledCloseButton,
  StyledFormGrid,
  StyledFormGroup,
  StyledFormInput,
  StyledLabel,
  StyledModalCard,
  StyledModalFooter,
  StyledModalHeader,
  StyledModalTitle,
  StyledOrderRef,
  StyledOrderSummary,
  StyledOverlay,
  StyledPaymentOption,
  StyledPaymentOptions,
  StyledPrimaryButton,
  StyledSecondaryButton,
  StyledSuccessBox,
  StyledSuccessIcon,
  StyledSuccessMessage,
  StyledSuccessTitle,
  StyledSummaryItem,
  StyledSummaryItemInfo,
  StyledSummaryRow,
  StyledTextarea,
} from './styles';
import { CartItem } from './types';

interface CheckoutModalProps {
  items: CartItem[];
  onClose: () => void;
  onComplete: () => void;
}

type PaymentMethod = 'qi-balance' | 'bank-transfer' | 'cod';

const CheckoutModal: React.FC<CheckoutModalProps> = ({
  items,
  onClose,
  onComplete,
}) => {
  const [submitted, setSubmitted] = useState(false);
  const [orderRef] = useState(
    () => `SDG-${Math.floor(100000 + Math.random() * 900000)}`
  );
  const [payment, setPayment] = useState<PaymentMethod>('qi-balance');
  const [form, setForm] = useState({
    company: '',
    contactName: '',
    phone: '',
    email: '',
    address: '',
    notes: '',
  });

  const subtotal = items.reduce(
    (sum, item) => sum + item.qty * item.product.priceQI,
    0
  );
  const shipping = subtotal > 0 ? 250 : 0;
  const total = subtotal + shipping;

  const isFormValid =
    form.company &&
    form.contactName &&
    form.phone &&
    form.email &&
    form.address;

  const handleField = (key: keyof typeof form) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid) return;
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <StyledOverlay>
        <StyledModalCard
          onClick={(e) => e.stopPropagation()}
          style={{ maxWidth: '32rem' }}
        >
          <StyledSuccessBox>
            <StyledSuccessIcon>✓</StyledSuccessIcon>
            <StyledSuccessTitle>Order placed successfully</StyledSuccessTitle>
            <StyledSuccessMessage>
              We&apos;ve received your bulk order. Our supply team will reach out
              within 24 hours to confirm stock and finalize delivery details.
            </StyledSuccessMessage>
            <StyledOrderRef>
              Order reference: <span>{orderRef}</span>
            </StyledOrderRef>
            <StyledPrimaryButton type='button' onClick={onComplete}>
              Back to Marketplace
            </StyledPrimaryButton>
          </StyledSuccessBox>
        </StyledModalCard>
      </StyledOverlay>
    );
  }

  return (
    <StyledOverlay onClick={onClose}>
      <StyledModalCard
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '64rem' }}
      >
        <StyledModalHeader>
          <StyledModalTitle>Checkout</StyledModalTitle>
          <StyledCloseButton onClick={onClose} type='button' aria-label='Close'>
            <IconClose />
          </StyledCloseButton>
        </StyledModalHeader>

        <form onSubmit={handleSubmit}>
          <StyledCheckoutBody>
            <div>
              <StyledCheckoutSection>
                <StyledCheckoutSectionTitle>
                  Business Details
                </StyledCheckoutSectionTitle>
                <StyledFormGrid>
                  <StyledFormGroup fullWidth>
                    <StyledLabel htmlFor='checkout-company'>
                      Company / Business Name
                    </StyledLabel>
                    <StyledFormInput
                      id='checkout-company'
                      type='text'
                      value={form.company}
                      onChange={handleField('company')}
                      placeholder='e.g. Demo Merchant LLC'
                      required
                    />
                  </StyledFormGroup>
                  <StyledFormGroup>
                    <StyledLabel htmlFor='checkout-contact-name'>
                      Contact Name
                    </StyledLabel>
                    <StyledFormInput
                      id='checkout-contact-name'
                      type='text'
                      value={form.contactName}
                      onChange={handleField('contactName')}
                      placeholder='Full name'
                      required
                    />
                  </StyledFormGroup>
                  <StyledFormGroup>
                    <StyledLabel htmlFor='checkout-phone'>Phone</StyledLabel>
                    <StyledFormInput
                      id='checkout-phone'
                      type='tel'
                      value={form.phone}
                      onChange={handleField('phone')}
                      placeholder='+966 5X XXX XXXX'
                      required
                    />
                  </StyledFormGroup>
                  <StyledFormGroup fullWidth>
                    <StyledLabel htmlFor='checkout-email'>Email</StyledLabel>
                    <StyledFormInput
                      id='checkout-email'
                      type='email'
                      value={form.email}
                      onChange={handleField('email')}
                      placeholder='you@business.com'
                      required
                    />
                  </StyledFormGroup>
                </StyledFormGrid>
              </StyledCheckoutSection>

              <StyledCheckoutSection>
                <StyledCheckoutSectionTitle>
                  Delivery Address
                </StyledCheckoutSectionTitle>
                <StyledFormGroup>
                  <StyledLabel htmlFor='checkout-address'>
                    Warehouse / Receiving Address
                  </StyledLabel>
                  <StyledTextarea
                    id='checkout-address'
                    value={form.address}
                    onChange={handleField('address')}
                    placeholder='Street, building, city, postal code'
                    required
                  />
                </StyledFormGroup>
                <StyledFormGroup style={{ marginTop: '0.75rem' }}>
                  <StyledLabel htmlFor='checkout-notes'>
                    Delivery Notes (optional)
                  </StyledLabel>
                  <StyledTextarea
                    id='checkout-notes'
                    value={form.notes}
                    onChange={handleField('notes')}
                    placeholder='Receiving hours, dock instructions, etc.'
                  />
                </StyledFormGroup>
              </StyledCheckoutSection>

              <StyledCheckoutSection>
                <StyledCheckoutSectionTitle>
                  Payment Method
                </StyledCheckoutSectionTitle>
                <StyledPaymentOptions>
                  <StyledPaymentOption selected={payment === 'qi-balance'}>
                    <input
                      type='radio'
                      name='payment'
                      checked={payment === 'qi-balance'}
                      onChange={() => setPayment('qi-balance')}
                    />
                    <span>💳</span>
                    Pay from QI Balance
                  </StyledPaymentOption>
                  <StyledPaymentOption selected={payment === 'bank-transfer'}>
                    <input
                      type='radio'
                      name='payment'
                      checked={payment === 'bank-transfer'}
                      onChange={() => setPayment('bank-transfer')}
                    />
                    <span>🏦</span>
                    Bank Transfer (Net 7)
                  </StyledPaymentOption>
                  <StyledPaymentOption selected={payment === 'cod'}>
                    <input
                      type='radio'
                      name='payment'
                      checked={payment === 'cod'}
                      onChange={() => setPayment('cod')}
                    />
                    <span>📦</span>
                    Cash on Delivery
                  </StyledPaymentOption>
                </StyledPaymentOptions>
              </StyledCheckoutSection>
            </div>

            <StyledOrderSummary>
              <StyledCheckoutSectionTitle>
                Order Summary
              </StyledCheckoutSectionTitle>
              {items.map((item) => (
                <StyledSummaryItem key={item.product.id}>
                  <StyledSummaryItemInfo>
                    <strong>{item.product.name}</strong>
                    <span>
                      {item.qty.toLocaleString()} × {item.product.priceQI.toLocaleString()} QI
                    </span>
                  </StyledSummaryItemInfo>
                  <div style={{ fontWeight: 600 }}>
                    {(item.qty * item.product.priceQI).toLocaleString()} QI
                  </div>
                </StyledSummaryItem>
              ))}

              <StyledSummaryRow>
                <span>Subtotal</span>
                <span>{subtotal.toLocaleString()} QI</span>
              </StyledSummaryRow>
              <StyledSummaryRow>
                <span>Shipping</span>
                <span>{shipping.toLocaleString()} QI</span>
              </StyledSummaryRow>
              <StyledSummaryRow bold>
                <span>Total</span>
                <span>{total.toLocaleString()} QI</span>
              </StyledSummaryRow>
            </StyledOrderSummary>
          </StyledCheckoutBody>

          <StyledModalFooter>
            <StyledSecondaryButton type='button' onClick={onClose}>
              Back to Cart
            </StyledSecondaryButton>
            <StyledPrimaryButton type='submit' disabled={!isFormValid}>
              Place Order · {total.toLocaleString()} QI
            </StyledPrimaryButton>
          </StyledModalFooter>
        </form>
      </StyledModalCard>
    </StyledOverlay>
  );
};

export default CheckoutModal;
